import { isEmpty, camelCase, isObject, uniq, transform, isArray, omit, pick, groupBy } from 'lodash-es';
import moment from 'moment';
import { TIME_FORMAT, LABEL_FREQUENCY, FREQUENCY_TYPE } from '../../constants/disruptions';
import { DATE_FORMAT_DDMMYYYY as DATE_FORMAT } from '../dateUtils';
import VEHICLE_TYPES from '../../types/vehicle-types';
import { STATUSES, PASSENGER_IMPACT_RANGE } from '../../types/disruptions-types';
import SEARCH_RESULT_TYPE from '../../types/search-result-types';
import { calculateActivePeriods } from '../recurrence';

export const PAGE_SIZE = 50;

export const formatCreatedUpdatedTime = time => moment(time).format(`${DATE_FORMAT} ${TIME_FORMAT}`);

export const momentFromDateTime = (date, time) => {
    if (time && date) {
        return moment(`${date}T${time}:00`, `${DATE_FORMAT}T${TIME_FORMAT}:ss`);
    }
    return undefined;
};

export const generateDisruptionActivePeriods = (disruption) => {
    if (disruption.recurrent) {
        return calculateActivePeriods(disruption.recurrencePattern, Number(disruption.duration), [], false);
    }

    const startTimeMoment = momentFromDateTime(disruption.startDate, disruption.startTime);
    const endTimeMoment = momentFromDateTime(disruption.endDate, disruption.endTime);

    return [{
        startTime: startTimeMoment ? startTimeMoment.unix() : undefined,
        endTime: endTimeMoment ? endTimeMoment.unix() : undefined,
    }];
};

export const isStartTimeValid = (startDate, startTime, openingTime, recurrent = false) => startTime !== '24:00'
    && moment(startTime, TIME_FORMAT, true).isValid()
    && (!recurrent || moment(`${startDate}T${startTime}:00`, `${DATE_FORMAT}T${TIME_FORMAT}:ss`).isSameOrAfter(openingTime, 'minute'));

export const isEndTimeValid = (endDate, endTime, startDate, startTime) => {
    if (isEmpty(endTime)) {
        return true;
    }

    const endTimeMoment = moment(`${endDate}T${endTime}:00`, `${DATE_FORMAT}T${TIME_FORMAT}:ss`);

    return moment(endTime, TIME_FORMAT, true).isValid()
        && moment(`${startDate}T${startTime}:00`, `${DATE_FORMAT}T${TIME_FORMAT}:ss`).isSameOrBefore(endTimeMoment, 'minute');
};

export const isEndDateValid = (endDate, startDate, recurrent = false) => {
    if (isEmpty(endDate) && !recurrent) {
        return true;
    }

    if (isEmpty(endDate) && recurrent) {
        return false;
    }

    return moment(endDate, DATE_FORMAT, true).isValid()
        && moment(endDate, DATE_FORMAT).isSameOrAfter(moment(startDate, DATE_FORMAT), 'day')
        && (!recurrent || moment(endDate, DATE_FORMAT).isSameOrAfter(moment(), 'day'));
};

export const isStartDateValid = (startDate, openingTime, recurrent = false) => moment(startDate, DATE_FORMAT, true).isValid()
    && (!recurrent || moment(startDate, DATE_FORMAT).isSameOrAfter(openingTime, 'day'));

export const isDurationValid = (duration, recurrent) => !recurrent || (!isEmpty(duration) && (Number.isInteger(+duration) && +duration > 0 && +duration < 25));

export const isStartDateTimeEarlierThanNow = (startDate, startTime) => {
    const startDateTime = moment(`${startDate}T${startTime}:00`, `${DATE_FORMAT}T${TIME_FORMAT}:ss`, true);
    if (!startDateTime.isValid()) return false;

    if (!startDateTime.isSame(moment(), 'day')) return false;

    return startDateTime.isBefore(moment(), 'minute');
};

export const buildSubmitBody = (disruption, routes, stops, workarounds) => {
    const modes = [...routes.map(route => VEHICLE_TYPES[route.routeType].type),
        ...stops.filter(stop => stop.routeId).map(routeByStop => VEHICLE_TYPES[routeByStop.routeType].type)];
    const routesToRequest = routes.map(({ routeId, routeShortName, routeType, type, directionId, stopId, stopCode, stopName, stopLat, stopLon }) => ({
        routeId,
        routeShortName,
        routeType,
        type,
        ...(stopCode !== undefined && {
            directionId,
            stopId,
            stopCode,
            stopName,
            stopLat,
            stopLon,
        }),
    }));
    const stopsToRequest = stops.map(entity => omit(entity, ['shapeWkt']));
    return {
        ...disruption,
        mode: uniq(modes).join(', '),
        affectedEntities: [...routesToRequest, ...stopsToRequest],
        ...(workarounds && { workarounds }),
    };
};

const getMode = disruption => [
    ...(disruption.affectedEntities?.affectedRoutes || [])
        .map(route => VEHICLE_TYPES[route?.routeType]?.type)
        .filter(Boolean),

    ...(disruption.affectedEntities?.affectedStops || [])
        .map(stop => VEHICLE_TYPES[stop?.routeType]?.type)
        .filter(Boolean),
];

export const filterWorkaroundsByAffectedEntity = (workarounds, affectedRoutes, affectedStops) => workarounds.filter((workaround) => {
    if (workaround.type === 'stop') {
        return (affectedStops.some(affectedStop => workaround.stopCode === affectedStop.stopCode)
            || affectedRoutes.some(affectedRoute => affectedRoute?.stopCode !== null && workaround.stopCode === affectedRoute.stopCode)
        );
    }
    if (workaround.type === 'route') {
        return (affectedRoutes.some(affectedRoute => workaround.routeShortName === affectedRoute.routeShortName)
            || affectedStops.some(affectedStop => affectedStop?.routeShortName !== null && workaround.routeShortName === affectedStop.routeShortName));
    }
    if (workaround.type === 'all') {
        return workarounds.length === 1;
    }

    return false;
});

export const getRecurrenceDates = (startDate, startTime, endDate) => {
    const recurrenceDates = {};
    if (startDate && startTime) {
        recurrenceDates.dtstart = momentFromDateTime(startDate, startTime).tz('UTC', true).toDate();
    }
    if (endDate) {
        recurrenceDates.until = momentFromDateTime(endDate, startTime || '00:00').tz('UTC', true).toDate();
    }
    return recurrenceDates;
};

const calculateEndTimeMoment = (disruptionEndDate, endTime) => {
    if (isEmpty(disruptionEndDate) || isEmpty(endTime)) {
        return undefined;
    }
    return moment.isMoment(endTime)
        ? endTime
        : momentFromDateTime(disruptionEndDate, endTime);
};

export const buildDisruptionSubmitBody = (disruption, incidentStatus, incidentCause, isEditMode, incidentEndTimeMoment, incidentRecurrent) => {
    const disruptionEndDate = disruption.endDate;
    const recurrenceDates = incidentRecurrent
        ? getRecurrenceDates(disruption.startDate, disruption.startTime, disruptionEndDate)
        : undefined;

    const startDate = disruption.startDate || (disruption.startTime ? moment(disruption.startTime).format(DATE_FORMAT) : '');
    const startTime = disruption.startTime || (disruption.startDate ? '00:00' : undefined);
    let startTimeMoment = disruption.startDate || disruption.startTime ? momentFromDateTime(startDate, startTime) : undefined;

    const endTimeMoment = calculateEndTimeMoment(disruptionEndDate, disruption.endTime);
    const modes = getMode(disruption);
    const routesToRequest = disruption.affectedEntities.affectedRoutes.map((
        { routeId, routeShortName, routeType, type, directionId, stopId, stopCode, stopName, stopLat, stopLon, diversionIds },
    ) => ({
        routeId,
        routeShortName,
        routeType,
        type,
        ...(stopCode !== undefined && {
            directionId,
            stopId,
            stopCode,
            stopName,
            stopLat,
            stopLon,
        }),
        ...(diversionIds && { diversionIds }),
    }));
    const stopsToRequest = disruption.affectedEntities.affectedStops.map(entity => omit(entity, ['shapeWkt']));

    const workarounds = disruption.workarounds?.length > 0 ? filterWorkaroundsByAffectedEntity(disruption.workarounds, routesToRequest, stopsToRequest) : disruption.workarounds;
    const isStatusBecomeResolved = incidentStatus === STATUSES.RESOLVED && disruption.status !== STATUSES.RESOLVED;
    if (disruption.status === STATUSES.NOT_STARTED && isStatusBecomeResolved) {
        startTimeMoment = incidentEndTimeMoment;
    }
    return {
        ...disruption,
        ...(isEditMode ? { } : { status: incidentStatus }),
        ...(isEditMode ? { } : { cause: incidentCause }),
        endTime: endTimeMoment,
        endDate: disruptionEndDate,
        startTime: startTimeMoment,
        mode: uniq(modes).join(', '),
        affectedEntities: [...routesToRequest, ...stopsToRequest],
        ...(isStatusBecomeResolved && incidentEndTimeMoment ? { status: STATUSES.RESOLVED, endTime: incidentEndTimeMoment } : { }),
        workarounds,
        url: '',
        recurrent: incidentRecurrent,
        ...(incidentRecurrent && {
            recurrencePattern: {
                ...disruption.recurrencePattern,
                ...recurrenceDates,
            },
        }),
    };
};

const getRecurrentPatterForIncident = (incident) => {
    const byweekday = [...new Set([...incident.disruptions.flatMap(d => d.recurrencePattern.byweekday), ...incident.recurrencePattern.byweekday])]
        .filter(x => x != null).sort((a, b) => a - b);
    let recurrenceDates = {};
    if (incident.status === STATUSES.DRAFT) {
        if (incident.startTime) {
            const startTimeMoment = moment.isMoment(incident.startTime) ? incident.startTime : moment(incident.startTime);
            if (moment.isMoment(startTimeMoment) && startTimeMoment.isValid()) {
                recurrenceDates = getRecurrenceDates(startTimeMoment.format(DATE_FORMAT), startTimeMoment.format(TIME_FORMAT), incident.endDate);
            }
        }
    } else {
        recurrenceDates = getRecurrenceDates(incident.startTime.format(DATE_FORMAT), incident.startTime.format(TIME_FORMAT), incident.endDate);
    }
    return {
        byweekday,
        freq: incident.recurrencePattern.freq,
        ...recurrenceDates,
    };
};

const shouldUpdateStartTime = (earliestStartTime, incidentStartTime) => {
    if (!earliestStartTime?.isValid() || !incidentStartTime) {
        return false;
    }
    const momentStartTime = moment.isMoment(incidentStartTime) ? incidentStartTime : moment(incidentStartTime);
    return moment.isMoment(momentStartTime) && momentStartTime.isValid() && earliestStartTime.isBefore(momentStartTime);
};

const shouldUpdateEndTime = (latestEndTime, incident, allResolved) => {
    if (!latestEndTime || incident.recurrent || !incident.endTime) {
        return false;
    }
    const isEndTimeAfter = latestEndTime.isAfter(incident.endTime);
    const shouldResolve = incident.status !== STATUSES.RESOLVED && allResolved;
    return isEndTimeAfter || shouldResolve;
};

const calculateValuesForRecurrentIncident = (incident) => {
    let endDate;
    if (incident.status === STATUSES.DRAFT) {
        const endDates = [
            ...incident.disruptions
                .map(disruption => moment(disruption.endDate, DATE_FORMAT, true))
                .filter(m => m.isValid()),
            ...(incident.endDate ? [moment(incident.endDate, DATE_FORMAT, true)] : []),
        ].filter(m => m.isValid());
        if (endDates.length > 0) {
            endDate = moment.max(endDates).format(DATE_FORMAT);
        } else {
            endDate = incident.endDate || '';
        }
    } else {
        const endDates = [
            ...incident.disruptions
                .map(disruption => moment(disruption.endDate, DATE_FORMAT, true))
                .filter(m => m.isValid()),
            moment(incident.endDate, DATE_FORMAT, true),
        ].filter(m => m.isValid());
        endDate = moment.max(endDates).format(DATE_FORMAT);
    }
    const durations = [...incident.disruptions.flatMap(d => d.duration), ...incident.duration];
    const duration = String(Math.max(...durations.map(Number)));
    return {
        endDate,
        recurrencePattern: getRecurrentPatterForIncident({ ...incident, endDate }),
        duration,
    };
};

export const buildIncidentSubmitBody = (incident, isEditMode) => {
    const modes = incident.disruptions.flatMap(disruption => getMode(disruption));
    const disruptions = incident.disruptions.flatMap(disruption => buildDisruptionSubmitBody(
        disruption,
        incident.status,
        incident.cause,
        isEditMode,
        incident.endTime,
        incident.recurrent,
    ));
    const allResolved = disruptions.every(disruption => disruption.status === STATUSES.RESOLVED);
    const updatedIncident = {
        ...incident,
        mode: uniq(modes).join(', '),
        disruptions,
        ...(allResolved && { status: STATUSES.RESOLVED }),
        url: '',
    };
    const earliestStartTime = moment.min(disruptions.map(disruption => disruption.startTime).filter(Boolean));
    if (shouldUpdateStartTime(earliestStartTime, incident.startTime)) {
        updatedIncident.startTime = earliestStartTime;
    }

    const endTimes = disruptions.map(disruption => disruption.endTime).filter(endTime => endTime != null);
    const latestEndTime = endTimes.length > 0 ? moment.max(endTimes) : null;
    if (shouldUpdateEndTime(latestEndTime, incident, allResolved)) {
        updatedIncident.endTime = latestEndTime;
    }
    const result = {
        ...updatedIncident,
        ...(incident.recurrent && calculateValuesForRecurrentIncident(updatedIncident)),
    };
    if (incident.recurrent && incident.endDate) {
        result.endDate = incident.endDate;
    }
    if (incident.recurrent && result.endTime && moment.isMoment(result.endTime)) {
        result.endTime = result.endTime.toISOString();
    }
    return result;
};

export const getStatusForEffect = (disruption) => {
    const now = moment();
    const startTimeMoment = momentFromDateTime(disruption.startDate, disruption.startTime);

    if (startTimeMoment < now) {
        return { status: STATUSES.IN_PROGRESS };
    }
    return { status: STATUSES.NOT_STARTED };
};

const transformKeysToCamelCase = obj => transform(obj, (acc, value, key, target) => {
    const camelKey = isArray(target) ? key : camelCase(key);
    acc[camelKey] = isObject(value) ? transformKeysToCamelCase(value) : value;
});

export const toCamelCaseKeys = entities => transformKeysToCamelCase(entities);

export const transformIncidentNo = (disruptionId) => {
    if (!disruptionId) return null;

    return `DISR${disruptionId.toString().padStart(6, '0')}`;
};

export const transformParentSourceIdNo = (id) => {
    if (!id) return null;

    return `CCD${id.toString().padStart(6, '0')}`;
};

export const recurrenceRadioOptions = isRecurrent => ({
    title: LABEL_FREQUENCY,
    formGroupClass: 'disruption-creation__wizard-select-details-frequency',
    checkedKey: isRecurrent ? '1' : '0',
    itemOptions: [{ key: '0', value: FREQUENCY_TYPE.ONCE }, { key: '1', value: FREQUENCY_TYPE.RECURRING }],
});

export const getStatusOptions = (startDate, startTime, now, status) => {
    let statuses = Object.values(STATUSES);
    if (status !== STATUSES.DRAFT) statuses = statuses.filter(s => s !== STATUSES.DRAFT);

    const startDateTime = moment(`${startDate}T${startTime}:00`, `${DATE_FORMAT}T${TIME_FORMAT}:ss`, true);
    if (!startDateTime || !startDateTime.isValid()) {
        return Object.values(statuses);
    }
    if (startDateTime.isAfter(now)) {
        return Object.values(statuses).filter(s => s !== STATUSES.IN_PROGRESS);
    }

    return Object.values(statuses).filter(s => s !== STATUSES.NOT_STARTED);
};

/**
 * this function takes a list of stops and groups them by their parent station, those without a parent station are added to the array 'noParentStation'.
 */
export const groupStopsByRouteElementByParentStation = (list) => {
    const result = new Map();
    list.forEach((element) => {
        if (element.parentStationStopId) {
            const parent = {
                stopId: element.parentStationStopId,
                stopCode: element.parentStationStopCode,
                stopName: element.parentStationStopName,
                stopLat: element.parentStationStopLat,
                stopLon: element.parentStationStopLon,
                directionId: element.directionId,
            };
            const parentAsString = JSON.stringify(parent);
            if (!result.has(parentAsString)) {
                result.set(parentAsString, []);
            }
            result.set(parentAsString, [...result.get(parentAsString), element]);
        } else {
            if (!result.has(undefined)) {
                result.set(undefined, []);
            }
            result.set(undefined, [...result.get(undefined), element]);
        }
    });
    return result;
};

export const getDeduplcatedAffectedRoutes = affectedEntities => [...new Set(affectedEntities.filter(entity => entity.routeId).map(({ routeShortName }) => routeShortName))];

export const getDeduplcatedAffectedStops = affectedEntities => [...new Set(affectedEntities.filter(entity => entity.stopCode).map(({ stopCode }) => stopCode))];

export const filterOnlyRouteParams = route => pick(route, ['routeId', 'routeShortName', 'routeType', 'routeColor', 'shapeWkt', 'agencyId', 'agencyName',
    'text', 'category', 'icon', 'valueKey', 'labelKey', 'type']);
export const filterOnlyStopParams = stop => pick(stop, ['stopId', 'stopName', 'stopCode', 'locationType', 'stopLat', 'stopLon', 'parentStation',
    'platformCode', 'text', 'category', 'icon', 'valueKey', 'labelKey', 'type', 'groupId']);

const { ROUTE, STOP, STOP_GROUP } = SEARCH_RESULT_TYPE;

export const formatStop = (stop, text = null, category = null, icon = null) => ({
    stopId: stop.stop_id,
    stopName: stop.stop_name,
    stopCode: stop.stop_code,
    locationType: stop.location_type,
    stopLat: stop.stop_lat,
    stopLon: stop.stop_lon,
    parentStation: stop.parent_station,
    platformCode: stop.platform_code,
    routeType: stop.route_type,
    text: text ?? `${stop.stop_code} - ${stop.stop_name}`,
    category,
    icon,
    valueKey: 'stopCode',
    labelKey: 'stopCode',
    type: STOP.type,
});

export const formatStopsWithGroup = (stops, groupId) => stops.map(stop => ({
    ...formatStop(stop),
    groupId,
}));

export const formatStopsInStopGroup = (stopGroups, allStops) => {
    const stops = [];
    stopGroups.forEach((group) => {
        if (!group.stops[0].value) {
            stops.push(...group.stops);
        } else {
            const groupStops = group.stops.map((stop) => {
                let foundStop = allStops[stop.value];

                if (!foundStop) {
                    foundStop = {
                        stop_id: `-${stop.value}`,
                        text: `${stop.value}`,
                        stop_name: `${stop.value}`,
                        stop_code: 'Not Found',
                    };
                }

                return foundStop;
            });

            stops.push(...formatStopsWithGroup(groupStops, group.groupId));
        }
    });

    return groupBy(stops, 'groupId');
};

export const entityToItemTransformers = {
    [ROUTE.type]: entity => ({
        routeId: entity.data.route_id,
        routeType: entity.data.route_type,
        routeShortName: entity.data.route_short_name,
        agencyName: entity.data.agency_name,
        agencyId: entity.data.agency_id,
        text: entity.text,
        category: entity.category,
        icon: entity.icon,
        valueKey: 'routeId',
        labelKey: 'routeShortName',
        type: ROUTE.type,
    }),
    [STOP.type]: entity => formatStop(entity.data, entity.text, entity.category, entity.icon),
    [STOP_GROUP.type]: entity => ({
        groupId: entity.data.id,
        groupName: entity.data.title,
        stops: entity.data.stops,
        valueKey: 'groupId',
        labelKey: 'groupName',
        type: STOP_GROUP.type,
        category: entity.category,
    }),
};

export const itemToEntityTransformers = {
    [ROUTE.type]: item => ({
        text: item.routeShortName,
        data: {
            route_id: item.routeId,
            route_type: item.routeType,
            route_short_name: item.routeShortName,
            agency_name: item.agencyName,
            agency_id: item.agencyId,
        },
        category: item.category,
        icon: item.icon,
    }),
    [STOP.type]: item => ({
        text: item.text,
        data: {
            stop_id: item.stopId,
            stop_name: item.stopName,
            stop_code: item.stopCode,
            location_type: item.locationType,
            stop_lat: item.stopLat,
            stop_lon: item.stopLon,
            parent_station: item.parentStation,
            platform_code: item.platformCode,
            route_type: item.routeType,
        },
        category: item.category,
        icon: item.icon,
    }),
    [STOP_GROUP.type]: item => ({
        text: item.groupName,
        data: {
            group_id: item.groupId,
        },
        category: item.category,
        icon: item.icon,
    }),
};

export const getPassengerCountRange = (count) => {
    if (count < 500) return PASSENGER_IMPACT_RANGE.LOWER_THAN_500;
    if (count >= 500 && count <= 5000) return PASSENGER_IMPACT_RANGE.BETWEEN_500_5000;
    if (count >= 5001 && count <= 15000) return PASSENGER_IMPACT_RANGE.BETWEEN_5001_15000;
    if (count >= 15001 && count <= 40000) return PASSENGER_IMPACT_RANGE.BETWEEN_15001_40000;
    return PASSENGER_IMPACT_RANGE.GREATER_THAN_40000;
};

export const isRecurringPeriodInvalid = (disruption) => {
    const { recurrencePattern, duration } = disruption;
    return (
        isEmpty(recurrencePattern?.byweekday)
        || isEmpty(recurrencePattern?.dtstart)
        || isEmpty(recurrencePattern?.until)
        || !(Number.isInteger(+duration) && +duration > 0)
    );
};

const buildDuration = (disruption) => {
    const startTime = moment(disruption.startTime);
    const endTime = moment(disruption.endTime);
    const now = moment();
    let cutoff = now;
    let duration;

    if (now <= startTime) {
        return undefined;
    }

    if (disruption.status === STATUSES.DRAFT && disruption.activePeriods?.length === 0) {
        return undefined;
    }

    if (disruption.status === STATUSES.RESOLVED || endTime <= now) {
        cutoff = endTime;
    }

    if (!disruption.recurrent) {
        duration = cutoff.diff(startTime, 'seconds');
    }

    if (disruption.recurrent) {
        const cutoffEpoch = cutoff.unix();
        duration = disruption.activePeriods
            ?.filter(activePeriod => activePeriod.startTime < cutoffEpoch)
            .map(activePeriod => ({
                startTime: activePeriod.startTime,
                endTime: Math.min(activePeriod.endTime, cutoffEpoch),
            }))
            .reduce((sum, activePeriod) => sum + (activePeriod.endTime - activePeriod.startTime), 0);
    }

    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;
    return { hours, minutes, seconds };
};

export const getDuration = (disruption) => {
    const duration = buildDuration(disruption);
    if (!duration) return '-';
    const { hours, minutes, seconds } = duration;
    return `${hours} hours, ${minutes} minutes, and ${seconds} seconds`;
};

export const getDurationWithoutSeconds = (disruption) => {
    const duration = buildDuration(disruption);
    if (!duration) return '-';
    const { hours, minutes } = duration;
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '-';
    return `${hours} hours, ${minutes} minutes`;
};

export const buildDisruptionsQuery = (filters) => {
    const { statuses, stopId, stopCode, onlyWithStops, includeDrafts } = filters;

    const queryParams = [];

    if (statuses) {
        queryParams.push(statuses.map(status => `statuses=${status}`).join('&'));
    }
    if (stopId) {
        queryParams.push(`stopId=${stopId}`);
    }
    if (stopCode) {
        queryParams.push(`stopCode=${stopCode}`);
    }
    if (onlyWithStops) {
        queryParams.push(`onlyWithStops=${onlyWithStops}`);
    }
    if (includeDrafts) {
        queryParams.push(`includeDraft=${includeDrafts}`);
    } else {
        queryParams.push('includeDraft=false');
    }

    return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
};
